import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  UserIcon,
  MapPinIcon,
  ClockIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowRightIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import adminAPI from '../utils/api';
import toast from 'react-hot-toast';
import ConfirmationModal from './ConfirmationModal';

const SessionDetailModal = ({ session, isOpen, onClose, onSessionUpdate }) => {
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [operationLoading, setOperationLoading] = useState(false);

  useEffect(() => {
    if (isOpen && session) {
      fetchSessionDetails();
    }
  }, [isOpen, session]);

  const fetchSessionDetails = async () => {
    setLoading(true);
    try {
      // Fetch bookings and waitlist for this session
      const [bookingsData, waitlistData] = await Promise.all([
        adminAPI.getSessionBookings(session.id),
        adminAPI.getSessionWaitlist(session.id)
      ]);

      setBookings(bookingsData || []);
      setWaitlist(waitlistData || []);
    } catch (error) {
      console.error('Error fetching session details:', error);
      setBookings([]);
      setWaitlist([]);
    } finally {
      setLoading(false);
    }
  };

  // Booking management functions
  const handleCancelBooking = (booking) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Annuler la réservation',
      message: `Êtes-vous sûr de vouloir annuler la réservation de ${booking.client?.full_name} ?`,
      type: 'danger',
      onConfirm: () => cancelBooking(booking.id)
    });
  };

  const handleMoveToWaitlist = (booking) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Déplacer vers la liste d\'attente',
      message: `Êtes-vous sûr de vouloir déplacer ${booking.client?.full_name} vers la liste d'attente ?`,
      type: 'warning',
      onConfirm: () => moveBookingToWaitlist(booking.id)
    });
  };

  const handleConfirmWaitlist = (waitlistEntry) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Confirmer la réservation',
      message: `Êtes-vous sûr de vouloir confirmer la réservation de ${waitlistEntry.client?.full_name} ?`,
      type: 'success',
      onConfirm: () => confirmWaitlistEntry(waitlistEntry.id)
    });
  };

  const handleCancelWaitlist = (waitlistEntry) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Supprimer de la liste d\'attente',
      message: `Êtes-vous sûr de vouloir supprimer ${waitlistEntry.client?.full_name} de la liste d'attente ?`,
      type: 'danger',
      onConfirm: () => cancelWaitlistEntry(waitlistEntry.id)
    });
  };

  const handleDeleteBooking = (booking) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Supprimer définitivement',
      message: `Êtes-vous sûr de vouloir supprimer définitivement la réservation de ${booking.client?.full_name} ?`,
      type: 'danger',
      onConfirm: () => deleteBooking(booking.id)
    });
  };

  const cancelBooking = async (bookingId) => {
    setOperationLoading(true);
    try {
      await adminAPI.updateBooking(bookingId, { status: 'cancelled' });
      toast.success('Réservation annulée avec succès');

      // Update local state immediately to show cancelled status
      setBookings(prev => prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: 'cancelled' }
          : booking
      ));

      // Fetch fresh data from server
      setTimeout(() => {
        fetchSessionDetails();
        // Notify parent to refresh session data (capacity counts)
        if (onSessionUpdate) {
          onSessionUpdate();
        }
      }, 1000); // Increased delay to ensure backend has processed
    } catch (error) {
      toast.error('Erreur lors de l\'annulation de la réservation');
      console.error('Error cancelling booking:', error);
    } finally {
      setOperationLoading(false);
      setConfirmationModal({ ...confirmationModal, isOpen: false });
    }
  };

  const moveBookingToWaitlist = async (bookingId) => {
    setOperationLoading(true);
    try {
      // Find the booking being moved
      const booking = bookings.find(b => b.id === bookingId);
      if (!booking) {
        toast.error('Réservation introuvable');
        return;
      }

      const response = await adminAPI.updateBooking(bookingId, { status: 'waitlist' });
      toast.success('Réservation déplacée vers la liste d\'attente');

      // Update local state immediately for better UX
      setBookings(prev => prev.filter(b => b.id !== bookingId));

      // If response contains waitlist entry data, use it
      if (response.waitlist_entry) {
        setWaitlist(prev => [...prev, response.waitlist_entry]);
      } else {
        // Fallback: create temporary waitlist entry
        setWaitlist(prev => [...prev, {
          id: `temp-${bookingId}`,
          client: booking.client,
          session: booking.session,
          client_service: booking.client_service,
          position: prev.length + 1,
          joined_at: new Date().toISOString()
        }]);
      }

      // Fetch fresh data from server
      setTimeout(() => {
        fetchSessionDetails();
        // Notify parent to refresh session data (capacity counts)
        if (onSessionUpdate) {
          onSessionUpdate();
        }
      }, 1000); // Increased delay to ensure backend has processed
    } catch (error) {
      toast.error('Erreur lors du déplacement vers la liste d\'attente');
      console.error('Error moving booking to waitlist:', error);
    } finally {
      setOperationLoading(false);
      setConfirmationModal({ ...confirmationModal, isOpen: false });
    }
  };

  const confirmWaitlistEntry = async (waitlistId) => {
    setOperationLoading(true);
    try {
      // First get the waitlist entry
      const waitlistEntry = waitlist.find(w => w.id === waitlistId);
      if (!waitlistEntry) {
        toast.error('Entrée de liste d\'attente introuvable');
        return;
      }

      // Use the new confirm waitlist endpoint
      const response = await adminAPI.confirmWaitlistEntry(waitlistId);

      toast.success('Réservation confirmée avec succès');

      // Update local state immediately
      setWaitlist(prev => prev.filter(w => w.id !== waitlistId));

      // Add the new booking to the list
      if (response.booking) {
        setBookings(prev => [...prev, response.booking]);
      } else {
        // Fallback: create temporary booking entry
        setBookings(prev => [...prev, {
          id: `temp-confirmed-${waitlistId}`,
          client: waitlistEntry.client,
          session: waitlistEntry.session,
          client_service: waitlistEntry.client_service,
          status: 'confirmed',
          booked_at: new Date().toISOString()
        }]);
      }

      // Fetch fresh data from server
      setTimeout(() => {
        fetchSessionDetails();
        // Notify parent to refresh session data (capacity counts)
        if (onSessionUpdate) {
          onSessionUpdate();
        }
      }, 1000); // Increased delay to ensure backend has processed
    } catch (error) {
      toast.error('Erreur lors de la confirmation de la réservation');
      console.error('Error confirming waitlist entry:', error);
    } finally {
      setOperationLoading(false);
      setConfirmationModal({ ...confirmationModal, isOpen: false });
    }
  };

  const cancelWaitlistEntry = async (waitlistId) => {
    setOperationLoading(true);
    try {
      await adminAPI.deleteWaitlistEntry(waitlistId);
      toast.success('Supprimé de la liste d\'attente');

      // Update local state immediately
      setWaitlist(prev => prev.filter(w => w.id !== waitlistId));

      // Fetch fresh data from server
      setTimeout(() => fetchSessionDetails(), 500);
    } catch (error) {
      toast.error('Erreur lors de la suppression de la liste d\'attente');
      console.error('Error cancelling waitlist entry:', error);
    } finally {
      setOperationLoading(false);
      setConfirmationModal({ ...confirmationModal, isOpen: false });
    }
  };

  const deleteBooking = async (bookingId) => {
    setOperationLoading(true);
    try {
      await adminAPI.deleteBooking(bookingId);
      toast.success('Réservation supprimée définitivement');

      // Update local state immediately
      setBookings(prev => prev.filter(b => b.id !== bookingId));

      // Fetch fresh data from server
      setTimeout(() => {
        fetchSessionDetails();
        // Notify parent to refresh session data (capacity counts)
        if (onSessionUpdate) {
          onSessionUpdate();
        }
      }, 1000); // Increased delay to ensure backend has processed
    } catch (error) {
      toast.error('Erreur lors de la suppression de la réservation');
      console.error('Error deleting booking:', error);
    } finally {
      setOperationLoading(false);
      setConfirmationModal({ ...confirmationModal, isOpen: false });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'text-green-600 bg-green-50';
      case 'booked':
        return 'text-blue-600 bg-blue-50';
      case 'cancelled':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'available':
        return 'Disponible';
      case 'booked':
        return 'Réservé';
      case 'cancelled':
        return 'Annulé';
      default:
        return status;
    }
  };

  if (!isOpen || !session) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Détails de la séance
            </h2>
            {session.title && (
              <p className="text-sm text-gray-600 mt-1">{session.title}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Session Info */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <ClockIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Date et heure</p>
                  <p className="font-medium">
                    {session.date ? format(new Date(session.date), 'EEEE d MMMM yyyy', { locale: fr }) : 'Date non disponible'}
                  </p>
                  <p className="text-sm text-gray-600">
                    {session.start_time?.slice(0, 5)} - {session.end_time?.slice(0, 5)}
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Coach</p>
                  <p className="font-medium">{session.coach?.user?.full_name}</p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Lieu</p>
                  <p className="font-medium">{session.location?.name}</p>
                </div>
              </div>
            </div>

            {/* Status & Capacity */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(session.status)}`}>
                  {getStatusText(session.status)}
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <UsersIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Capacité</p>
                  <p className="font-medium">
                    {session.current_bookings || 0} / {session.max_capacity} réservations
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-600">Liste d'attente</p>
                  <p className="font-medium">{waitlist.length} / 3 personnes</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bookings & Waitlist */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Bookings */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                Réservations ({bookings.length})
              </h3>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : bookings.length > 0 ? (
                <div className="space-y-3">
                  {bookings.map((booking) => (
                    <div key={booking.id} className={`rounded-lg p-4 ${
                      booking.status === 'confirmed' ? 'bg-green-50 border border-green-200' :
                      booking.status === 'cancelled' ? 'bg-red-50 border border-red-200' :
                      'bg-gray-50 border border-gray-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {booking.client?.full_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {booking.client?.email}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Réservé le {booking.booked_at ? format(new Date(booking.booked_at), 'dd/MM/yyyy à HH:mm') : 'Date inconnue'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {booking.status === 'confirmed' ? 'Confirmé' :
                             booking.status === 'cancelled' ? 'Annulé' :
                             booking.status}
                          </span>
                          <div className="flex space-x-1">
                            {booking.status === 'confirmed' && (
                              <>
                                <button
                                  onClick={() => handleMoveToWaitlist(booking)}
                                  disabled={operationLoading}
                                  className="p-1 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Déplacer vers la liste d'attente"
                                >
                                  {operationLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-yellow-600"></div>
                                  ) : (
                                    <ArrowRightIcon className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => handleCancelBooking(booking)}
                                  disabled={operationLoading}
                                  className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Annuler la réservation"
                                >
                                  {operationLoading ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                  ) : (
                                    <TrashIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </>
                            )}
                            {booking.status === 'cancelled' && (
                              <button
                                onClick={() => handleDeleteBooking(booking)}
                                disabled={operationLoading}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Supprimer définitivement"
                              >
                                {operationLoading ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                ) : (
                                  <TrashIcon className="h-4 w-4" />
                                )}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <UsersIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune réservation</p>
                </div>
              )}
            </div>

            {/* Waitlist */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <ClipboardDocumentListIcon className="h-5 w-5 text-yellow-600 mr-2" />
                Liste d'attente ({waitlist.length})
              </h3>
              
              {loading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                </div>
              ) : waitlist.length > 0 ? (
                <div className="space-y-3">
                  {waitlist.map((wait) => (
                    <div key={wait.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">
                            {wait.client?.full_name}
                          </p>
                          <p className="text-sm text-gray-600">
                            {wait.client?.email}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Ajouté le {wait.joined_at ? format(new Date(wait.joined_at), 'dd/MM/yyyy à HH:mm') : 'Date inconnue'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Position {wait.position}
                          </span>
                          <div className="flex space-x-1">
                            <button
                              onClick={() => handleConfirmWaitlist(wait)}
                              disabled={operationLoading}
                              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Confirmer la réservation"
                            >
                              {operationLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                              ) : (
                                <CheckCircleIcon className="h-4 w-4" />
                              )}
                            </button>
                            <button
                              onClick={() => handleCancelWaitlist(wait)}
                              disabled={operationLoading}
                              className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Supprimer de la liste d'attente"
                            >
                              {operationLoading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <TrashIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <ClipboardDocumentListIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Aucune personne en attente</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => !operationLoading && setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        type={confirmationModal.type}
        loading={operationLoading}
      />

      {/* Loading Overlay */}
      {operationLoading && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 rounded-lg">
          <div className="bg-white p-4 rounded-lg flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
            <span className="text-gray-700">Traitement en cours...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionDetailModal;
